import * as Sentry from '@sentry/react';
import { apiCall } from '../components/restaurant/RestaurantBackend';
import { config } from '../config/environment';
import { dummyServerPerformanceHistoryData } from '../data/dummyData';
import { calculateCriteriaCounts as sharedCalculateCriteriaCounts } from '../utils/criteriaUtils';
import { computeScore } from '../utils/scoreUtils';
import {
  getServerPerfShifts,
  getServerPerfCriteria,
  getServerPerfRevelAttribution,
  getServerStatsRevelOrderCounts,
} from './scopedReadsService';

export interface RevelMetricAttribution {
  upsellCount: number;
  purchaseCount: number;
  totalValue: number;
}

export interface ServerPerformanceHistoryEntry {
  date: string;
  shiftId: string;
  startTime: string;
  endTime: string;
  duration: number;
  interactions: number;
  greetings: number;
  score: number;
  premiumMenu: number;
  itemMention: number;
  itemDescription: number;
  drinkPromotions: number;
  /**
   * Percentage (0–100) of fulfilled criteria per metric key, populated for
   * every criteria ``type`` present in the shift's interaction data. Lets the
   * table render role-specific columns (e.g. add_on_upsell, dessert_upsell)
   * without relying on the legacy metric_1/2/3 fields.
   */
  metricValues?: Record<string, number>;
  type: string;
  role?: string | null;
  prompt_template_version_id?: string | null;
  revelAttribution?: {
    metric_1: RevelMetricAttribution;
    metric_2: RevelMetricAttribution;
    metric_3: RevelMetricAttribution;
    drink_promotion: RevelMetricAttribution;
  };
}

export class ServerPerformanceHistoryService {
  /**
   * Fetch the server's profile/badge/challenge state from the same mobile
   * endpoints the Flutter Profile screen uses. Keeps the web dashboard's
   * All-Time Stats grid in sync with what the user sees in the app.
   */
  public static async getServerMobileStats(serverId: string): Promise<{
    totalXP: number;
    totalShifts: number;
    currentStreak: number;
    longestStreak: number;
    badgesEarned: number;
    badgesTotal: number;
    challengeTitle: string;
    challengeCurrent: number;
    challengeTarget: number;
    challengeCompleted: boolean;
  }> {
    const empty = {
      totalXP: 0,
      totalShifts: 0,
      currentStreak: 0,
      longestStreak: 0,
      badgesEarned: 0,
      badgesTotal: 0,
      challengeTitle: '',
      challengeCurrent: 0,
      challengeTarget: 0,
      challengeCompleted: false,
    };

    if (config.useDummyData) {
      return {
        ...empty,
        totalXP: 5767,
        totalShifts: 183,
        currentStreak: 5,
        longestStreak: 8,
        badgesEarned: 4,
        badgesTotal: 10,
        challengeTitle: '$100 Challenge',
        challengeCurrent: 4,
        challengeTarget: 4,
        challengeCompleted: true,
      };
    }

    // feat-133 Batch-4 (ADR 0004 DP-4b): keyed by server_id (nav state) — the
    // by-name server lookup is eliminated. Absent id → empty (the /api/mobile/*
    // endpoints require a server_id anyway).
    if (!serverId) {
      return empty;
    }
    const qs = `server_id=${encodeURIComponent(serverId)}`;

    const getJson = async (path: string): Promise<any | null> => {
      try {
        const resp = await apiCall(path, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!resp.ok) {
          console.warn(`mobile endpoint ${path} -> ${resp.status}`);
          return null;
        }
        return await resp.json();
      } catch (err) {
        console.warn(`mobile endpoint ${path} failed`, err);
        return null;
      }
    };

    const [stats, badges, challenge] = await Promise.all([
      getJson(`/api/mobile/profile-stats?${qs}`),
      getJson(`/api/mobile/badges?${qs}`),
      getJson(`/api/mobile/challenge-progress?${qs}`),
    ]);

    const badgeList: any[] = Array.isArray(badges?.badges) ? badges.badges : [];

    return {
      totalXP: stats?.total_xp ?? 0,
      totalShifts: stats?.total_shifts ?? 0,
      currentStreak: stats?.current_streak ?? 0,
      longestStreak: stats?.longest_streak ?? 0,
      badgesEarned: badgeList.filter(b => b?.earned).length,
      badgesTotal: badgeList.length,
      challengeTitle: challenge?.title ?? '',
      challengeCurrent: challenge?.current ?? 0,
      challengeTarget: challenge?.target ?? 0,
      challengeCompleted: !!challenge?.completed,
    };
  }

  private static calculateCriteriaCounts(criteriaItems: any[]): {
    upsellCount: number;
    mentionCount: number;
    describeCount: number;
    drinkPromotionCount: number;
    initialGreetingCount: number;
  } {
    const result = sharedCalculateCriteriaCounts(criteriaItems);
    return {
      upsellCount: result.metricCounts['metric_1'] || 0,
      mentionCount: result.metricCounts['metric_2'] || 0,
      describeCount: result.metricCounts['metric_3'] || 0,
      drinkPromotionCount: result.drinkPromotionCount,
      initialGreetingCount: result.initialGreetingCount,
    };
  }

  public static async getServerPerformanceHistory(
    serverId: string,
    tenantId: string,
    startDate: string,
    endDate: string
  ): Promise<ServerPerformanceHistoryEntry[]> {
    // In dev mode, return dummy data
    if (config.useDummyData) {
      console.log('[DEV MODE] Returning dummy server performance history data');
      return dummyServerPerformanceHistoryData;
    }

    // feat-133 Batch-4: keyed by server_id (nav state) + tenant (auth context) —
    // the by-name server lookup is ELIMINATED (injection + cross-tenant
    // name-collision risk; ADR 0004 DP-4a). The server is verified in-tenant
    // server-side (404 foreign). Absent id/tenant → [] is behavior-identical
    // (the old by-name path returned [] when the name didn't resolve).
    if (!serverId || !tenantId) {
      return [];
    }

    try {
      // Get all shifts for the server + date range (scoped endpoint; replaces the
      // by-server_id shifts SQL — same columns incl type/role/prompt_template_version_id).
      const shifts = await getServerPerfShifts(tenantId, serverId, {
        start: startDate,
        end: endDate,
      });

      if (shifts.length === 0) {
        console.log(`No shifts found for server ${serverId} in date range ${startDate} to ${endDate}`);
        return [];
      }

      // Raw shift IDs for the scoped batch endpoints (a foreign shift_id → 0 rows).
      const shiftIds = shifts.map((shift: any) => shift.shift_id);

      // Criteria — batched in groups of 30 shift IDs (scoped endpoint; same
      // aliased columns interaction_id/interaction_data, no server_id).
      const BATCH_SIZE = 30;
      const criteriaResults = [];
      for (let i = 0; i < shiftIds.length; i += BATCH_SIZE) {
        const batchShiftIds = shiftIds.slice(i, i + BATCH_SIZE);
        const batchResults = await getServerPerfCriteria(tenantId, serverId, batchShiftIds);
        criteriaResults.push(...batchResults);
      }

      // Fetch Revel order counts per shift to use as greetings when available (non-fatal)
      let revelOrderCountMap: Record<string, number> = {};
      try {
        for (let i = 0; i < shiftIds.length; i += BATCH_SIZE) {
          const batchShiftIds = shiftIds.slice(i, i + BATCH_SIZE);
          // Reuse the Batch-3 endpoint (byte-identical to #12's order-counts).
          const batchResults = await getServerStatsRevelOrderCounts(tenantId, batchShiftIds);
          for (const row of batchResults) {
            revelOrderCountMap[row.shift_id] = parseInt(row.order_count, 10) || 0;
          }
        }
        console.log('Revel order counts loaded for', Object.keys(revelOrderCountMap).length, 'shifts');
      } catch (err) {
        console.warn('Revel order count fetch failed (tables may not exist yet):', err);
      }

      // Fetch Revel POS attribution data per shift (non-fatal)
      let revelAttributionMap: Record<string, Record<string, { purchaseCount: number; totalValue: number }>> = {};
      try {
        const revelResults = [];
        for (let i = 0; i < shiftIds.length; i += BATCH_SIZE) {
          const batchShiftIds = shiftIds.slice(i, i + BATCH_SIZE);
          // Per-SHIFT attribution (grouped by rosl.shift_id) — its own endpoint,
          // distinct from Batch-3's per-server one.
          const batchResults = await getServerPerfRevelAttribution(tenantId, serverId, batchShiftIds);
          revelResults.push(...batchResults);
        }

        for (const row of revelResults) {
          const sid = row.shift_id;
          if (!revelAttributionMap[sid]) revelAttributionMap[sid] = {};
          revelAttributionMap[sid][row.match_rule] = {
            purchaseCount: parseInt(row.purchase_count, 10) || 0,
            totalValue: parseFloat(row.total_value) || 0,
          };
        }
        console.log('Revel attribution data loaded for', Object.keys(revelAttributionMap).length, 'shifts');
      } catch (revelErr) {
        console.warn('Revel attribution fetch failed (tables may not exist yet):', revelErr);
      }

      // Process each shift to calculate performance metrics
      const performanceHistory: ServerPerformanceHistoryEntry[] = [];

      for (const shift of shifts) {
        // Calculate shift duration
        let durationMinutes = 0;
        if (shift.shift_created && shift.shift_ended) {
          const startTime = new Date(shift.shift_created.endsWith('Z')
            ? shift.shift_created
            : `${shift.shift_created}Z`);

          const endTime = new Date(shift.shift_ended.endsWith('Z')
            ? shift.shift_ended
            : `${shift.shift_ended}Z`);

          durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
        }

        // Get criteria for this shift
        const shiftCriteria = criteriaResults.filter((criteria: any) => criteria.shift_id === shift.shift_id);
        const criteriaCounts = this.calculateCriteriaCounts(shiftCriteria);

        // Role configs can define their own metric keys (e.g. Cashier uses
        // add_on_upsell/dessert_upsell/… instead of metric_1/2/3). Discover
        // every criteria type present in this shift's interactions and count
        // fulfillments for all of them, so the table's dynamic columns light
        // up instead of showing 0% for role-specific keys.
        const discoveredKeys = new Set<string>();
        for (const item of shiftCriteria) {
          const arr = (item as any)?.interaction_data?.service_criteria;
          if (Array.isArray(arr)) {
            for (const c of arr) {
              if (c?.type) discoveredKeys.add(c.type);
            }
          }
        }
        const allMetricKeys = Array.from(discoveredKeys);
        const fullCounts = allMetricKeys.length > 0
          ? sharedCalculateCriteriaCounts(shiftCriteria, allMetricKeys)
          : null;

        // Use Revel order count as greetings if available and > 0, otherwise fall back to interaction count
        const revelOrderCount = revelOrderCountMap[shift.shift_id] || 0;
        const maxUpsells = Math.max(criteriaCounts.upsellCount, criteriaCounts.mentionCount, criteriaCounts.describeCount, criteriaCounts.drinkPromotionCount);
        const baseGreetingCount = revelOrderCount > 0 ? revelOrderCount : criteriaCounts.initialGreetingCount;
        const greetingCount = Math.max(baseGreetingCount, maxUpsells);

        // Calculate percentages
        const upsellPercent = greetingCount > 0
          ? Math.round((criteriaCounts.upsellCount / greetingCount) * 100)
          : 0;
        const mentionPercent = greetingCount > 0
          ? Math.round((criteriaCounts.mentionCount / greetingCount) * 100)
          : 0;
        const describePercent = greetingCount > 0
          ? Math.round((criteriaCounts.describeCount / greetingCount) * 100)
          : 0;
        const drinkPercent = greetingCount > 0
          ? Math.round((criteriaCounts.drinkPromotionCount / greetingCount) * 100)
          : 0;

        // Percentages keyed by every metric type the annotator actually
        // emitted for this shift.
        const metricValues: Record<string, number> = {};
        if (fullCounts && greetingCount > 0) {
          for (const key of allMetricKeys) {
            const count = fullCounts.metricCounts[key] || 0;
            metricValues[key] = Math.round((count / greetingCount) * 100);
          }
        }

        const averageScore = computeScore([upsellPercent, mentionPercent, describePercent], drinkPercent);

        // Format date for display
        // The shift_created is in UTC format from the database (e.g., '2025-11-29T15:57:08')
        // We need to convert it to Pacific time to get the correct date
        let formattedDate = '';
        if (shift.shift_created) {
          try {
            // Parse the UTC date string (ensure it's treated as UTC by appending 'Z' if not present)
            const utcDateString = shift.shift_created.endsWith('Z') ? shift.shift_created : `${shift.shift_created}Z`;
            const shiftDateUtc = new Date(utcDateString);
            
            if (!isNaN(shiftDateUtc.getTime())) {
              // Convert UTC date to Pacific time and extract the date part
              // Use Intl.DateTimeFormat to format in Pacific timezone
              const formatter = new Intl.DateTimeFormat('en-CA', {
                timeZone: 'America/Los_Angeles',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              });
              formattedDate = formatter.format(shiftDateUtc);
            } else {
              console.warn('Invalid shift_created date:', shift.shift_created);
              formattedDate = 'Invalid Date';
            }
          } catch (error) {
            console.error('Error parsing shift_created date:', error, shift.shift_created);
            formattedDate = 'Invalid Date';
          }
        } else {
          console.warn('Missing shift_created for shift:', shift.shift_id);
          formattedDate = 'No Date';
        }

        // Build revel attribution for this shift
        const shiftRevel = revelAttributionMap[shift.shift_id] || {};
        const emptyAttribution = { upsellCount: 0, purchaseCount: 0, totalValue: 0 };
        const buildAttribution = (matchRule: string, upsellCount: number): RevelMetricAttribution => ({
          upsellCount,
          purchaseCount: shiftRevel[matchRule]?.purchaseCount || 0,
          totalValue: shiftRevel[matchRule]?.totalValue || 0,
        });

        performanceHistory.push({
          date: formattedDate,
          shiftId: shift.shift_id,
          startTime: shift.shift_created,
          endTime: shift.shift_ended,
          duration: durationMinutes,
          interactions: shift.interactions_count,
          greetings: greetingCount,
          score: averageScore,
          premiumMenu: upsellPercent,
          itemMention: mentionPercent,
          itemDescription: describePercent,
          drinkPromotions: drinkPercent,
          metricValues,
          type: shift.shift_type || 'dinner', // Default to dinner if type is not set
          role: shift.role || null,
          prompt_template_version_id: shift.prompt_template_version_id || null,
          revelAttribution: {
            metric_1: buildAttribution('metric_1', criteriaCounts.upsellCount),
            metric_2: buildAttribution('metric_2', criteriaCounts.mentionCount),
            metric_3: buildAttribution('metric_3', criteriaCounts.describeCount),
            drink_promotion: buildAttribution('drink_promotion', criteriaCounts.drinkPromotionCount),
          },
        });
      }

      return performanceHistory;

    } catch (error) {
      console.error('Error fetching server performance history:', error);
      Sentry.captureException(error);
      throw error;
    }
  }
}
